import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import {
  KitchenBoardGrid,
  KitchenBoardState,
  KitchenHeader,
  KitchenStatusBar,
} from '../../features/restaurant/kitchen';
import useApiErrorHandler from '../../hooks/useApiErrorHandler';
import useRestaurantKitchenConfig from '../../hooks/restaurant/useRestaurantKitchenConfig';
import useRestaurantKitchenOrders from '../../hooks/restaurant/useRestaurantKitchenOrders';
import WorkspaceLayout from '../../layout/WorkspaceLayout';
import BranchService from '../../services/branch/BranchService';
import { useAuthUser } from '../../hooks/auth/useAuthUser';

import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import { RestaurantKitchenOrderStatus } from '../../types/restaurant/RestaurantKitchenOrderTypes';

const RestaurantKitchen = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);

  const { handle } = useApiErrorHandler();
  const kitchen = useRestaurantKitchenOrders(branchId);
  const kitchenConfig = useRestaurantKitchenConfig(branchId);

  const boardBoxRef = useRef<HTMLDivElement>(null);
  // Modo enfocado: usa la Fullscreen API del navegador cuando está disponible y,
  // si el navegador la bloquea, cae a una superposición a pantalla dentro de la
  // app. En ambos casos el tablero se muestra compacto y con foco en los platos.
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => {
      const next = !prev;

      if (next) {
        boardBoxRef.current?.requestFullscreen?.().catch(() => undefined);
      } else if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => undefined);
      }

      return next;
    });
  }, []);

  useEffect(() => {
    const sync = () => {
      // Solo reacciona a la salida de fullscreen nativo (tecla Esc); la entrada
      // ya la maneja el toggle para cubrir el caso en que la API esté bloqueada.
      if (!document.fullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);

        if (items.length) {
          setBranchId(items[0].id);
        }
      })
      .catch(handle)
      .finally(() => setLoadingBranches(false));

    return () => kitchen.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPickBranch]);

  useEffect(() => {
    if (!branchId) return;

    kitchen.updateFilter('stationId', '');
    kitchen.loadStations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    if (!branchId) return;

    kitchen.load();

    const interval = window.setInterval(() => kitchen.load(true), 5000);

    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, kitchen.filters.stationId, kitchen.filters.status]);

  const counts = useMemo(
    () => ({
      pending: kitchen.list.filter((item) => item.status === 'PENDING').length,
      inPreparation: kitchen.list.filter(
        (item) => item.status === 'IN_PREPARATION'
      ).length,
      ready: kitchen.list.filter((item) => item.status === 'READY').length,
    }),
    [kitchen.list]
  );

  const boardContent = () => {
    if (kitchen.loading || loadingBranches) {
      return <KitchenBoardState type="loading" />;
    }

    if (!branchId) {
      return <KitchenBoardState type="noBranch" />;
    }

    if (kitchen.list.length === 0) {
      return <KitchenBoardState type="empty" />;
    }

    return (
      <KitchenBoardGrid
        orders={kitchen.list}
        stationId={kitchen.filters.stationId}
        actionKey={kitchen.actionKey}
        printStates={kitchen.printStates}
        parallelPreparation={kitchenConfig.config.parallelPreparationEnabled}
        showTimer={kitchenConfig.config.preparationTimerEnabled}
        focus={isFullscreen}
        showOrderActions={kitchenConfig.config.orderCompletionMode === 'MANUAL'}
        showItemActions={
          kitchenConfig.config.orderCompletionMode === 'MANUAL' ||
          !kitchenConfig.config.autoAdvanceItemsByTime
        }
        onStart={kitchen.start}
        onReady={kitchen.markReady}
        onStartItem={kitchen.startItem}
        onReadyItem={kitchen.markItemReady}
        onReprint={kitchen.reprint}
      />
    );
  };

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <div
          ref={boardBoxRef}
          className={`rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark ${
            isFullscreen
              ? 'fixed inset-0 z-999 flex flex-col overflow-y-auto rounded-none'
              : 'overflow-hidden'
          }`}
        >
          <KitchenHeader
            branches={branches}
            branchId={branchId}
            loadingBranches={loadingBranches}
            canPickBranch={canPickBranch}
            stations={kitchen.stations}
            stationId={kitchen.filters.stationId}
            status={kitchen.filters.status}
            compact={isFullscreen}
            onBranchChange={setBranchId}
            onStationChange={(stationId) =>
              kitchen.updateFilter('stationId', stationId)
            }
            onStatusChange={(status: RestaurantKitchenOrderStatus | '') =>
              kitchen.updateFilter('status', status)
            }
          />

          <KitchenStatusBar
            pending={counts.pending}
            inPreparation={counts.inPreparation}
            ready={counts.ready}
            stationScoped={Boolean(kitchen.filters.stationId)}
            lastUpdatedAt={kitchen.lastUpdatedAt}
            loading={kitchen.loading}
            refreshing={kitchen.refreshing}
            disabled={!branchId}
            fullscreen={isFullscreen}
            compact={isFullscreen}
            onRefresh={() => kitchen.load(true)}
            onToggleFullscreen={toggleFullscreen}
          />

          <div
            className={`bg-gray-3 p-5 dark:bg-boxdark-2 sm:p-7 ${
              isFullscreen ? 'flex-1' : 'min-h-[460px]'
            }`}
          >
            {boardContent()}
          </div>
        </div>
      </SectionWrapper>
    </WorkspaceLayout>
  );
};

export default RestaurantKitchen;
